import { useTranslation } from 'react-i18next';

import { Box, createIcon, Flex, Heading, List, ListItem, Text } from '@chakra-ui/react';

import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { StatsSection } from '~/components/stats-section';
import { GrowthIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

const CertificatePictogramIcon = createIcon({
  displayName: 'CertificatePictogramIcon',
  viewBox: '0 0 48 48',
  path: (
    <>
      <rect fill='#89E6FF' fill-opacity='0.16' height='48' rx='6' transform='matrix(1 0 0 -1 0 48)' width='48' />
      <circle cx='24.0479' cy='19.8331' fill='#89E6FF' opacity='0.8' r='13.7159' />
      <g opacity='0.5'>
        <path d='M15.121 42L30.6388 26.7089L23.8498 20.019L8.33203 35.3101L15.121 42Z' fill='#89E6FF' />
        <path d='M32.543 41.981L17.0253 26.6899L23.8143 20L39.332 35.2911L32.543 41.981Z' fill='#89E6FF' />
      </g>
    </>
  ),
});

const FastForwardPictogramIcon = createIcon({
  displayName: 'FastForwardPictogramIcon',
  viewBox: '0 0 48 48',
  path: (
    <>
      <path
        d='M0 42C0 45.3137 2.68629 48 6 48H42C45.3137 48 48 45.3137 48 42V6C48 2.68629 45.3137 0 42 0H6C2.68629 0 0 2.68629 0 6V42Z'
        fill='#E5A5FF'
        fill-opacity='0.16'
      />
      <path d='M42.3945 24.3001L23.4945 36.0001L23.4945 12.6001L42.3945 24.3001Z' fill='#E5A5FF' opacity='0.4' />
      <path d='M28.8984 24.3001L9.99844 36.0001L9.99844 12.6001L28.8984 24.3001Z' fill='#E5A5FF' />
    </>
  ),
});

const CoinPictogramIcon = createIcon({
  displayName: 'CoinPictogramIcon',
  viewBox: '0 0 48 48',
  path: (
    <>
      <path
        d='M0 42C0 45.3137 2.68629 48 6 48H42C45.3137 48 48 45.3137 48 42V6C48 2.68629 45.3137 0 42 0H6C2.68629 0 0 2.68629 0 6V42Z'
        fill='#FFB27D'
        fill-opacity='0.16'
      />
      <path d='M6.33203 39H41.332V31H6.33203V39Z' fill='#FFB27D' opacity='0.5' />
      <circle
        cx='14'
        cy='14'
        fill='#FFB27D'
        opacity='0.8'
        r='14'
        transform='matrix(4.37114e-08 1 1 -4.37114e-08 10 8)'
      />
    </>
  ),
});

const SignalPictogramIcon = createIcon({
  displayName: 'SignalPictogramIcon',
  viewBox: '0 0 48 48',
  path: (
    <>
      <path
        d='M42 2.62268e-07C45.3137 1.17422e-07 48 2.68629 48 6L48 42C48 45.3137 45.3137 48 42 48L6 48C2.68629 48 1.98073e-06 45.3137 1.83588e-06 42L2.62268e-07 6C1.17422e-07 2.68629 2.68629 1.98073e-06 6 1.83588e-06L42 2.62268e-07Z'
        fill='#7BA2FF'
        fill-opacity='0.16'
      />
      <path d='M39 40L39 8L31 8L31 40L39 40Z' fill='#7BA2FF' opacity='0.88' />
      <path d='M28 40L28 16L20 16L20 40L28 40Z' fill='#7BA2FF' opacity='0.66' />
      <path d='M17 40L17 28L9 28L9 40L17 40Z' fill='#7BA2FF' opacity='0.4' />
    </>
  ),
});

export function OutcomesSection() {
  const { t } = useTranslation();
  const stats = [
    {
      icon: (
        <SignalPictogramIcon
          height={{
            base: '40px',
            md: '48px',
          }}
          width={{
            base: '40px',
            md: '48px',
          }}
        />
      ),
      value: '35%',
      label: t('enterprise.overview.outcomes.stats.productivity'),
      theme: 'blue',
    },
    {
      icon: (
        <FastForwardPictogramIcon
          height={{
            base: '40px',
            md: '48px',
          }}
          width={{
            base: '40px',
            md: '48px',
          }}
        />
      ),
      value: '2x',
      label: t('enterprise.overview.outcomes.stats.timeToCompetency'),
      theme: 'purple',
    },
    {
      icon: (
        <CoinPictogramIcon
          height={{
            base: '40px',
            md: '48px',
          }}
          width={{
            base: '40px',
            md: '48px',
          }}
        />
      ),
      value: '$1.5M',
      label: t('enterprise.overview.outcomes.stats.savings'),
      theme: 'orange',
    },
    {
      icon: (
        <CertificatePictogramIcon
          height={{
            base: '40px',
            md: '48px',
          }}
          width={{
            base: '40px',
            md: '48px',
          }}
        />
      ),
      value: '90%',
      label: t('enterprise.overview.outcomes.stats.skillAttainment'),
      theme: 'cyan',
    },
  ];
  return (
    <StatsSection
      headerColor='blue.300'
      headerIcon={<GrowthIcon color='blue.300' height='18px' width='18px' />}
      headerSubtitle={t('enterprise.overview.outcomes.subtitle') ?? ''}
      footnote={
        <Text color='white' size='caption' textAlign='center'>
          <Box as='sup'>*</Box>
          {t('enterprise.overview.outcomes.footnote')}
        </Text>
      }
      headerDescription={
        <Text
          color='grey.200'
          margin='0 auto'
          maxWidth='720px'
          fontSize={{
            base: '0.875rem',
            md: '1rem',
          }}
          lineHeight={{
            base: '1.4',
            md: '1.5',
          }}
        >
          {t('enterprise.overview.outcomes.description')}
        </Text>
      }
      headerTitle={
        <Heading
          as='h2'
          color='white'
          fontWeight='600'
          letterSpacing='-2px'
          fontSize={{
            base: '1.75rem',
            md: '3.375rem',
          }}
          lineHeight={{
            base: '1.14',
            md: '1.1',
          }}
        >
          {t('enterprise.overview.outcomes.title')}
        </Heading>
      }
    >
      <List
        display='flex'
        gap='24px'
        flexDirection={{
          base: 'column',
          md: 'row',
        }}
      >
        {stats.map((stat) => (
          <ListItem
            key={stat.label}
            alignItems={{ base: 'center', md: 'flex-start' }}
            bgColor='hsla(222, 100%, 74%, 0.08)'
            border='1px solid'
            borderColor='#7BA2FF14'
            borderRadius='8px'
            display='flex'
            flex='1'
            flexDirection={{ base: 'row', md: 'column' }}
            gap={{
              base: '16px',
              md: '24px',
            }}
            padding={{
              base: '16px',
              md: '24px',
            }}
          >
            {stat.icon}
            <Flex
              gap={4}
              alignItems={{
                base: 'center',
                md: 'flex-start',
              }}
              flexDirection={{
                base: 'row',
                md: 'column',
              }}
            >
              <Heading
                as='p'
                color={`${stat.theme}.300`}
                fontSize={{ base: '2.625rem', md: '2.875rem' }}
                lineHeight='1'
              >
                {stat.value}
              </Heading>
              <HeadingWithEmphasis
                text={stat.label}
                emphasisProps={{
                  color: `${stat.theme}.300`,
                  display: 'inline-block',
                  fontWeight: '400',
                }}
                headingProps={{
                  as: 'p',
                  color: 'white',
                  fontSize: {
                    base: '0.75rem',
                    md: '0.8125rem',
                  },
                  lineHeight: '1.4',
                  fontWeight: '500',
                  flex: {
                    base: '1',
                    md: 'unset',
                  },
                  _after: {
                    content: '"*"',
                  },
                }}
              />
            </Flex>
          </ListItem>
        ))}
      </List>
    </StatsSection>
  );
}
